import { theme } from '@theme';
import { FC, useEffect, useState } from 'react';
import Modal from 'react-modal';
import { NotesCountMap, NoteView } from '../../../../types/Note';
import { Typography } from '../../../style/Typography';
import ConfirmationModal from '../confirmation-modal/ConfirmationModal';
import ModalTitle from '../ModalTitle';
import Notes from './Notes';

type NoteModalProps = {
  dealerId: string;
  leadId: string;
  fullName: string;
  isNotesModalOpen: boolean;
  notesCountMap: NotesCountMap[];
  vin?: string;
  hasRetry?: boolean;
  showTitleIcon?: boolean;
  titleIconType?: string;
  titleIconClassName?: string;
  hasCustomTitle?: boolean;
  titleComponent?: JSX.Element;
  titleClassName?: string;
  openNotesModal: (value: boolean) => void;
  updateNotesCount: (value: NotesCountMap) => void;
  getNotes: (leadId: string, dealerId: string) => Promise<NoteView[]>;
  postNotes: (
    leadId: string,
    noteInput: string,
    dealerId: string,
    vin?: string,
  ) => Promise<NoteView>;
  retry?: (id: string | number, dealerId: string, addMissingData: boolean) => Promise<void>;
};

const NoteModal: FC<NoteModalProps> = (props) => {
  const [noteInput, setNoteInput] = useState<string>('');
  const [confirmModalOpen, setConfirmModalOpen] = useState<boolean>(false);

  const placeholderText = `Add a note about ${props.fullName}...`;

  useEffect(() => {
    if (Modal.defaultStyles.overlay) {
      Modal.defaultStyles.overlay.backgroundColor = `rgb(0,0,0,.4)`;
      Modal.defaultStyles.overlay.zIndex = 205;
      Modal.defaultStyles.overlay.overflowY = `auto`;
    }
    Modal.setAppElement('#modalHandler');
  }, []);

  const handleCloseNotesModal = () => {
    if (noteInput.length > 0 && noteInput !== placeholderText) {
      setConfirmModalOpen(true);
    } else if (noteInput === '' || noteInput === placeholderText) {
      props.openNotesModal(false);
      setNoteInput('');
    }
  };

  const handleCancelClickConfirmationModal = () => {
    setConfirmModalOpen(false);
    props.openNotesModal(false);
    setNoteInput('');
  };

  const handleSubmitClickConfirmationModal = () => {
    setConfirmModalOpen(false);
  };

  const bodyComponent: JSX.Element = (
    <Typography size={theme.typography.h5}>
      If you don't save your changes, they will be lost. Are you sure you want to close this window?
    </Typography>
  );

  return (
    <div data-testid='note-modal' id='modalHandler'>
      <Modal
        isOpen={props.isNotesModalOpen}
        onRequestClose={handleCloseNotesModal}
        contentLabel='Note Modal'
        style={modalStyles}
        overlayClassName={'notes-modal-overlay'}
        ariaHideApp={false}
      >
        <ModalTitle
          bgColor={theme.palette.text.purple}
          title='Notes'
          size={theme.typography.h4}
          color={theme.palette.white}
          fontWeight={theme.typography.fontWeights.semibold}
          iconColor={theme.palette.white}
          handleIconClick={handleCloseNotesModal}
          showCloseIcon={true}
          height='56px'
          hasCustomTitle={props.hasCustomTitle}
          titleComponent={props.titleComponent}
          titleClassName={props.titleClassName}
          borderRadius='6px'
        />
        <Notes
          fullName={props.fullName}
          updateNotesCount={props.updateNotesCount}
          dealerId={props.dealerId}
          getNotes={props.getNotes}
          isNotesModalOpen={props.isNotesModalOpen}
          leadId={props.leadId}
          notesCountMap={props.notesCountMap}
          postNotes={props.postNotes}
          vin={props.vin}
          setNoteInput={setNoteInput}
          noteInput={noteInput}
          retry={props.retry}
        />
      </Modal>
      <ConfirmationModal
        isOpen={confirmModalOpen}
        handleCancelClick={handleCancelClickConfirmationModal}
        handleSubmitClick={handleSubmitClickConfirmationModal}
        cancelButtonText='Discard'
        submitButtonText='Keep Editing'
        title='Unsaved Changes'
        overlayClassName='notes-modal-overlay confirmation'
        contentClassName='notes-modal-confirmation-content'
        padding='20px 50px 20px 20px'
        minHeight='206px'
        minWidth='260px'
        showCloseIcon={true}
        bodyComponent={bodyComponent}
      />
    </div>
  );
};

export default NoteModal;

const modalStyles: Modal.Styles = {
  content: {
    top: '60px',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    transform: 'translateX(-50%)',
    maxWidth: '640px',
    width: '100%',
    zIndex: 205,
    padding: '0',
    overflow: 'visible',
    display: 'flex',
    flexDirection: 'column' as 'column',
    border: 0,
    boxShadow: '0 5px 15px rgb(0 0 0 / 50%)',
    borderRadius: '8px 8px 6px 6px',
  },
};
